import React, { useCallback, useMemo } from 'react'
import { FormAutosuggest, FormTextField, useWatch, useZionFormContext } from '@fs/zion-form'
import { ContentAdd, ContentDelete } from '@fs/zion-icon'
import {
  Button,
  Cell,
  Checkbox,
  CheckboxGroup,
  FlowGrid,
  IconButton,
  LabelSpacer,
  MenuOverlay,
  Paragraph,
  Row,
  Separator,
} from '@fs/zion-ui'
import {
  useSlsStateContext,
  getSlsTypesByKey,
  useSlsHighlightsContext,
  getNestedOrFloatingElementById,
  getDisplayLabelForFieldType,
} from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import { sortNamePartTypes } from '../utils/helpers'
import { namePartTypeKeys } from '../../shared/utils/helpers'
import useNameParts from '../hooks/useNameParts'
import { getHighlightIdsByFieldId } from '../../shared/utils/highlightUtils'
import HighlightButton from '../../panels/shared/HighlightButton'

/**
 * NamePartsForm component for managing and displaying editable name parts within the EssentialInformationForm component.
 *
 * This component allows users to add, remove, and edit various parts of a name (e.g., first name, last name, middle name).
 * It integrates with the useFieldArray hook for form state management and SLS for type definitions and highlights.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.field - The field object representing the current name entry.
 * @param {number} props.index - The index of the name entry in the names array.
 * @param {Object} props.fieldArrayMethods - Methods for manipulating the field array (add, remove, etc). The results of useFieldArray hook.
 * @param {boolean} [props.hideHeader=false] - When true, suppresses the primary / alternative name header label region.
 * @param {boolean} [props.hideHighlightButton=false] - When true, omits the per-field highlight button column.
 *
 * @returns {JSX.Element} The rendered NamePartsForm component.
 */
const NamePartsForm = ({
  field,
  index,
  fieldArrayMethods,
  hideHeader = false,
  hideHighlightButton = false,
  splitScreenImageArk,
}) => {
  const [t] = useTranslation()
  const { types: slsTypes, images } = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const formMethods = useZionFormContext()
  const { getValues, formState } = formMethods

  // Getting SLS types for names and fields only once
  const slsNameTypes = useMemo(() => getSlsTypesByKey(slsTypes, 'names'), [slsTypes])
  const slsFieldTypes = useMemo(() => getSlsTypesByKey(slsTypes, 'fields'), [slsTypes])

  // Filter SLS field types to only include those relevant for name parts
  const nameFieldTypes = useMemo(
    () =>
      slsFieldTypes
        .filter((type) => namePartTypeKeys.includes(type.fieldType))
        .sort((a, b) => sortNamePartTypes(a, b, 'fieldType')),
    [slsFieldTypes]
  )

  // Prepare options for the name type autosuggest
  const nameTypeAutosuggestOptions = useMemo(
    () => slsNameTypes.map((type) => ({ primaryText: type.displayLabel, key: type.nameType })),
    [slsNameTypes]
  )

  // We watch both 'parts' and 'removedParts' to re-render when a part is added or removed
  const [selectedNamePartTypes] = useWatch({
    name: [`names.${index}.parts`, `names.${index}.removedParts`],
  })

  // Determine which name parts are currently active (not removed) in the form
  const formNamePartFieldIdentifiers = selectedNamePartTypes?.map((type) => `names.${index}.${type}`) || []
  const currentNamePartFieldsInForm = getValues(formNamePartFieldIdentifiers)
  const activeNameParts = currentNamePartFieldsInForm?.filter((namePart) => namePart && !namePart?.removed)
  const activeNamePartTypes = activeNameParts?.map((part) => part.type).sort(sortNamePartTypes) || []

  // Handlers from the custom hook to manage name parts
  const { handleNamePartToggle, handleRemoveName } = useNameParts({ index, formMethods, fieldArrayMethods })

  // Handlers to manage highlight hover on focus/blur of input fields
  const handleInputFocus = useCallback(
    (fieldType) => {
      const fieldId = getValues(`names.${index}.${fieldType}.id`)
      const { mainEditHighlights } = h.getHighlights()
      const fieldHighlightsIds = getHighlightIdsByFieldId(mainEditHighlights, fieldId)

      h.hoverHighlights({ elementIds: fieldHighlightsIds })
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getValues, index]
  )

  const handleInputBlur = useCallback(() => {
    h.hoverHighlights({ elementIds: [] })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Prepares a field object for use in the HighlightButtonOld component
  const prepareFieldForHighlights = useCallback(
    (type) => {
      const currentField = getValues(`names.${index}.${type}`)

      return {
        ...currentField,
        displayLabel: getDisplayLabelForFieldType(type, slsTypes),
        name: `names.${index}.${type}.value`,
        fieldType: type,
        elementType: 'FIELD',
        ...getNestedOrFloatingElementById(images, currentField.id),
      }
    },
    [images, slsTypes, index, getValues]
  )

  // This is stub code for updating useFieldArray values after a save from the SharedHighlight panel. We will need it soon, but it has not been thoroughly tested
  // const handleHighlightSave = (data) => {
  //   console.info('Highlights saved. Logging this from NamePartsFormSection inside the EssentialInformationCard', {
  //     data,
  //   })
  //   updateFormAfterSharedFieldLink(fieldArrayMethods, data)
  // }

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="nano" key={field.id}>
      {index < 2 && !hideHeader && (
        <Paragraph size="sm">
          <strong>
            {index === 0 && t('essentialInformationForm.names.primaryName', 'Primary Name')}
            {index === 1 && t('essentialInformationForm.names.alternativeNames', 'Alternative Names')}
          </strong>
        </Paragraph>
      )}

      <Row gutters="xxs">
        <FormAutosuggest
          name={`names.${index}.type`}
          label={t('essentialInformationForm.names.nameType', 'Name Type')}
          suggestions={nameTypeAutosuggestOptions}
          listMaxHeight={500}
          data-testid={`name-type-autosuggest-${index}`}
          dense
          growX
          warningValidate={() => {
            // Check for validation errors on this specific name type field
            // Handle both React Hook Form structure and custom fieldArray names array structures
            const nameTypeError = formState.errors?.names?.[index]?.type
            const customValidationError = formState.errors?.names?.[index]

            if (nameTypeError?.message) {
              return nameTypeError.message
            }

            if (customValidationError?.message && typeof customValidationError.message === 'string') {
              return customValidationError.message
            }

            return null
          }}
        />
        {index > 0 && (
          <Cell>
            <LabelSpacer />
            <IconButton
              Icon={ContentDelete}
              title={t('essentialInformationForm.names.removeName', 'Remove Name')}
              onClick={() => handleRemoveName(index)}
              color="red"
              data-testid={`remove-name-button-${index}`}
              linkName="essential-information-remove-name"
            />
          </Cell>
        )}
      </Row>

      {activeNamePartTypes.map((visibleNamePartType) => (
        <Row gutters="xxs" key={visibleNamePartType}>
          <FormTextField
            name={`names.${index}.${visibleNamePartType}.value`}
            label={getDisplayLabelForFieldType(visibleNamePartType, slsTypes)}
            onFocus={() => handleInputFocus(visibleNamePartType)}
            onBlur={handleInputBlur}
            data-testid={`name-${index}-${visibleNamePartType}-field`}
            dense
            growX
          />
          {!hideHighlightButton && (
            <Cell>
              <LabelSpacer />
              <HighlightButton
                field={prepareFieldForHighlights(visibleNamePartType)}
                splitScreenImageArk={splitScreenImageArk}
                // onSave={handleHighlightSave}
                buttonProps={{
                  'data-testid': `name-${index}-${visibleNamePartType}-highlight-button`,
                }}
                growX
                iconOnly
              />
            </Cell>
          )}
        </Row>
      ))}

      <>
        <Separator size="nano" />
        <MenuOverlay
          menuButton={
            <Button
              Icon={ContentAdd}
              data-testid={`name-${index}-more-info-button`}
              linkName="essential-information-more-info"
              dense
            >
              {t('essentialInformationForm.names.moreInfo', 'More Info')}
            </Button>
          }
          aria-label={t('essentialInformationForm.names.selectNameFields', 'Select name fields to add')}
        >
          <Separator size="nano" />
          <CheckboxGroup
            label={t('essentialInformationForm.names.selectNameFields', 'Select name fields to add')}
            dense
          >
            {nameFieldTypes.sort(sortNamePartTypes).map((nameFieldType) => (
              <React.Fragment key={nameFieldType.fieldType}>
                <Checkbox
                  key={nameFieldType.fieldType}
                  name={`names.${index}.parts`}
                  value={nameFieldType.fieldType}
                  label={nameFieldType.displayLabel}
                  checked={activeNamePartTypes?.includes(nameFieldType.fieldType)}
                  onChange={(event) => handleNamePartToggle(event, nameFieldType.fieldType)}
                  data-testid={`name-part-${index}-${nameFieldType.fieldType}-checkbox`}
                  role="menuitemcheckbox"
                />
                <Separator size="nano" />
              </React.Fragment>
            ))}
          </CheckboxGroup>
        </MenuOverlay>
      </>
    </FlowGrid>
  )
}

export default NamePartsForm
